/**
 * VidyoMain class for Vidyo functionality.
 * This class loads Vidyo-related functionality and manages the video conference
 *
 * Vidyo provides three mechanisms for providing conference capabilities
 * 1. NPAPI plugin
 * 2. Chrome Extension (VidyoWebConnecter) coupled with Native Messaging Host (VidyoClientForWeb)
 * 3. WebRTC
 *
 * Which mechanism is used is dependent on the browser in use, and whether WebRTC is configured/available
 *
 * The VidyoMain.vidyoClient property will either be of type VidyoClient (for NPAPI or VidyoClientForWeb)
 * or of type WebRTCClient. Vidyo conference events are sent/received through VidyoMain.vidyoClient
 */

namespace SmartVideo {
    export class VidyoMain {
        timer: number;
        config: ISmartVideoConfig;

        // conference properties
        fullScreenShare: boolean;
        isSharing: boolean;
        sharesInCall: number;
        currentShareId: string;
        participantsInCall: number;
        newShareUri: string;
        // appWindowId property doesn't appear to be used at the moment
        appWindowId: any;
        sysDesktopId: string[];
        callState: string;
        progressStep: number;

        // configuration of behavior for this code
        useConsoleForLogging: boolean;
        useAlertsForLogging: boolean;
        useCallbackForOutEvents: boolean; // if false, use a JavaScript event instead of callback
        willStartOnLoad: boolean; // if true, will invoke start() on plug-in object from bodyLoaded()
        useLoginEvent: boolean;
        useWebRTC: boolean;
        isChrome: boolean;
        isFireFox: boolean;
        isSmartVideoAgent: boolean;
        browser: string;
        isJoinRequest: boolean;
        pluginVersion: string;
        chromeNMSVersion: string;

        urlPortalAddress: string;
        urlUser: string;
        urlPass: string;
        urlPortalUri: string;
        urlGuestName: string;
        urlRoomKey: string;
        urlRoomPin: string;
        urlEncoded: string;
        urlUseSignIn: string;
        urlInteractionId: string;
        urlMuteVideoOnJoin: boolean;
        urlMuteAudioOnJoin: boolean;
        webRTCSessionManager: string;
        ImagePath: string;

        // Toggle Divs
        vidyoArea: string;
        pluginHolder: string;
        videoElem: HTMLVideoElement;
        shareSelect: HTMLSelectElement;

        // Classes
        vidyoClient: IVidyoClient;
        chromeWrapper: ChromeWrapper;

        // Vidyo ended callback
        vidyoEndedCallback: Function;
        showConferenceCallback: Function;

        // Conference modal
        conferenceModal: HTMLElement;

        // Queue object, used to queue events that should be sent when conference starts
        inEventQueueForSend: Array<Object>;

        constructor(config: any, vidyoDetection: IVidyoDetection, vidyoArea: string, pluginHolder: string,
            ImagePath?: string, webRTCSessionManager?: string) {
            this.isChrome = vidyoDetection.isChrome;
            this.isFireFox = vidyoDetection.isFireFox;
            this.isSmartVideoAgent = false;
            this.browser = vidyoDetection.browserReport.browser.name;
            if (this.browser === 'SmartVideoAgent') {
                this.isSmartVideoAgent = true;
            }
            this.isJoinRequest = false;
            this.useWebRTC = vidyoDetection.useWebRTC;
            this.webRTCSessionManager = webRTCSessionManager;
            this.config = config;

            this.log('VidyoMain Chrome Detection: ' + this.isChrome);
            this.chromeNMSVersion = vidyoDetection.chromeNativeMessagingHostVersion;
            this.pluginVersion = vidyoDetection.pluginVersion;
            // init the Classes
            if (!vidyoDetection.useWebRTC) {
                this.vidyoClient = new VidyoClient(this.isChrome, this.browser);
            } else {
                this.vidyoClient = new WebRTCClient(this.isChrome, this.browser);
            }

            // in-call variables to manage display modes, etc
            this.fullScreenShare = false;
            this.isSharing = false;
            this.sharesInCall = 0;
            this.currentShareId = '-1';
            this.participantsInCall = 0;
            this.appWindowId = [];
            this.sysDesktopId = [];
            this.callState = 'Idle';

            // configuration of behavior for this code
            this.useConsoleForLogging = true;
            this.useAlertsForLogging = false;
            this.useCallbackForOutEvents = true; // if false, use a JavaScript event instead of callback
            this.willStartOnLoad = true; // if true, will invoke start() on plug-in object
            this.useLoginEvent = true;
            this.progressStep = 0;


            this.urlPortalAddress = '';
            this.urlUser = '';
            this.urlPass = '';
            this.urlPortalUri = '';
            this.urlGuestName = '';
            this.urlRoomKey = '';
            this.urlRoomPin = '';
            this.urlEncoded = '';
            this.urlInteractionId = '';
            this.urlMuteAudioOnJoin = false;
            this.urlMuteVideoOnJoin = false;
            if (ImagePath) {
                this.ImagePath = ImagePath;
            } else {
                this.ImagePath = './images/';
            }

            // Define Divs that hold/display Vidyo player
            this.vidyoArea = vidyoArea;
            this.pluginHolder = pluginHolder;

            this.conferenceModal = null;
            this.inEventQueueForSend = [];
        }

        initVidyo(url: string, isJoinRequest: boolean, vidyoEndedCallback: Function, showConferenceCallback?: Function) {
            // document.addEventListener("beforeunload", this.beforeUnload);
            this.vidyoEndedCallback = vidyoEndedCallback;
            this.showConferenceCallback = showConferenceCallback;
            this.isJoinRequest = isJoinRequest;
            this.parseUrlArgs(url);
            this.sendCustomerConnected();
            if (!this.useWebRTC) {
                if (this.isChrome || this.isSmartVideoAgent) {
                    this.startChromeWrapper();
                } else {
                    this.startPlugin();
                }
            } else {
                this.startWebRTCClient();
            }
        }

        beforeUnload() {
            /* Modern browsers no longer allow any action except to show a browser-generated warning dialog
            * So attempting to do anything other than returning a string which will be contained in the dialog
            * is useless. Commenting out the code just for historical purposes */

            // document.removeEventListener("beforeunload", this.beforeUnload);
            // this.setParticipantLimit(16);

            // if (this.callState !== "InConference") {
            //     var inEvent = {
            //         'type': "InEventCancel"
            //     };
            //     this.vidyoClient.sendEvent(inEvent, null);
            // }
            // this.sendDisconnect();
            // return null;
        }

        updateShareDropDown(response) {
            this.log(`updateShareDropDown(): ${JSON.stringify(response)}`);

            const transformedData = {
                windows: [],
                desktops: [],
                sharing: !(this.currentShareId === '-1')
            };
            let i: number,
                name,
                mywindow: { id; name; highlight: boolean },
                desktop: { id; name: string; highlight: boolean };

            // windows and desktops are separated in the response
            // you may choose not to expose all windows and/or desktops based on the use case. The filtering would happen here.
            for (i = 0; i < response.numApplicationWindows; i++) {
                name = (response.appWindowAppName[i] && response.appWindowAppName[i].length)
                    ? response.appWindowAppName[i] : response.appWindowName[i];
                mywindow = {
                    id: response.appWindowId[i],
                    name: name,
                    highlight: (this.currentShareId === response.appWindowId[i])
                };
                transformedData.windows.push(mywindow);
            }
            this.sysDesktopId.length = 0;
            for (i = 0; i < response.numSystemDesktops; i++) {
                // Custom text for sharing full displays
                let desktopName = 'Desktop ';
                desktopName = desktopName + (i + 1).toString();
                desktop = {
                    id: response.sysDesktopId[i],
                    name: desktopName,
                    highlight: (this.currentShareId === response.sysDesktopId[i])
                };
                transformedData.desktops.push(desktop);
                this.sysDesktopId.push(response.sysDesktopId[i]);
            }

            // var select = <HTMLSelectElement>document.getElementById("share_select");
            const select = this.shareSelect;

            for (i = select.options.length - 1; i >= 0; i--) {
                select.remove(i);
            }

            // note that using an HTML dropdown list allows the dropdown to appear over the video window.
            // Javascript and other more complex UI may not always overlay plugin/video divs in some browsers.
            if (this.isSharing) {
                const option = document.createElement('option');
                option.value = '-1';
                option.text = 'Stop Sharing';
                select.add(option);

            } else {
                const option = document.createElement('option');
                option.value = '-1';
                option.text = 'Select an App or Desktop to Share';
                select.add(option);
            }

            transformedData.windows.forEach(element => {
                if (element.highlight) {
                    const option = document.createElement('option');
                    option.value = element.id;
                    option.text = element.name;
                    option.selected = true;
                    select.add(option);
                } else {
                    const option = document.createElement('option');
                    option.value = element.id;
                    option.text = element.name;
                    select.add(option);
                }
            });

            transformedData.desktops.forEach(element => {
                if (element.highlight) {
                    const option = document.createElement('option');
                    option.value = element.id;
                    option.text = element.name;
                    option.selected = true;
                    select.add(option);
                } else {
                    const option = document.createElement('option');
                    option.value = element.id;
                    option.text = element.name;
                    select.add(option);
                }
            });
        }

        updateShareList(e: Event) {
            this.log('updateShareList()');
            const request = {
                'type': 'RequestGetWindowsAndDesktops'
            };
            this.shareSelect = <HTMLSelectElement>e.target;
            this.vidyoClient.sendRequest(request, this.updateShareDropDown.bind(this));
        }

        shareChanged(e: Event) {
            // keep track of what's newly shared to updated highlighting on share dropdown
            const selectShare = <HTMLSelectElement>e.target;
            this.currentShareId = selectShare.value;
            // shareId = -1 means stop sharing
            if (this.currentShareId === '-1') {
                this.isSharing = false;
                this.log('sending unshare event');
                this.sendUnshareEvent();
            } else {
                this.isSharing = true;
                this.log(`sending share event with id ${this.currentShareId}`);
                this.sendShareEvent(this.currentShareId);
            }
        }

        toggleConferenceButton(e: Event) {
            const togglebutton = <HTMLInputElement>e.target;
            const togglelabel = document.getElementById('lbl_' + togglebutton.id);
            this.log(togglebutton);
            switch (togglebutton.id) {
                case 'camera_toggle':
                    if (togglebutton.checked) {
                        togglelabel.innerText = 'Camera ON';
                        this.sendMuteCameraEvent(false);
                    } else {
                        togglelabel.innerText = 'Camera OFF';
                        // For NPAPI plugins, need to queue the event if plugin isn't loaded yet
                        this.sendMuteCameraEvent(true);
                    }
                    break;
                case 'share_toggle':
                    const inEvent = <any>{};
                    let msg;
                    if (togglebutton.checked) {
                        inEvent.type = 'InEventShare';
                        inEvent.shareType = 'DesktopWindow';
                        inEvent.window = '1';
                        togglelabel.innerText = 'Sharing In Progress';

                    } else {
                        inEvent.type = 'InEventUnshare';
                        togglelabel.innerText = 'Click to Share';
                    }
                    if (this.vidyoClient.sendEvent(inEvent)) {
                        msg = 'Sent ' + inEvent.type + ' successfully';
                    } else {
                        msg = 'Not in a call!';
                        alert(msg);
                    }
                    this.log(msg);
                    break;
                case 'microphone_toggle':
                    if (togglebutton.checked) {
                        togglelabel.innerText = 'Microphone ON';
                        this.sendMuteMicEvent(false);
                    } else {
                        togglelabel.innerText = 'Microphone OFF';
                        // For NPAPI plugins, need to queue the event if plugin isn't loaded yet
                        this.sendMuteMicEvent(true);
                    }
                    break;
                case 'speaker_toggle':
                    if (togglebutton.checked) {
                        togglelabel.innerText = 'Speaker ON';
                        this.sendMuteSpeakerEvent(false);
                    } else {
                        togglelabel.innerText = 'Speaker OFF';
                        this.sendMuteSpeakerEvent(true);
                    }
                    break;
                default:
                    break;
            }

            this.log('toggleConferenceButton: ' + togglebutton.id + ':' + togglebutton.checked);
        }

        clientGetNumParticipants(callback) {
            const request = {
                'type': 'RequestGetNumParticipants',
                'numParticipants': 0
            };
            this.vidyoClient.sendRequest(request, response => {
                this.log(`clientGetNumParticipants(): ${response}`);
                callback(response.numParticipants);
            });
        }

        clientGetParticipants(callback) {
            let actualParticipantCount = 0;
            const request = {
                'type': 'RequestGetParticipants',
                'numberParticipants': 0,
                'name': ''
            };
            this.vidyoClient.sendRequest(request, response => {
                if (response) {
                    const responseJson = JSON.stringify(response);
                    this.log(responseJson);
                } else {
                    this.log('Did not get a response object back to clientGetParticipants request');
                    return;
                };
                this.log(`clientGetParticipants(): ${response}`);
                actualParticipantCount = response.numberParticipants;
                // Eliminate the VidyoReplay Recorder participant and any Supervisor Monitor participants
                // Similar logic exists in WebRTCPlugin to prevent OutEventParticpantsChanged from being raised
                // when Recorder or Monitor joins/leaves
                for (let i = 0; i < response.name.length; i++) {
                    // Recorder is counted as a participant, so need to make sure we don't try to get it's stream
                    // Monitoring supervisors also don't send a stream, so exclude them from list of participants
                    // Effectively, we want to ignore these participants
                    if (response.name[i] === 'Recorder' || response.name[i].indexOf('-Monitor') >= 0) {
                        actualParticipantCount = actualParticipantCount - 1;
                    }
                }
                this.log('clientGetParticipants returned participant count: ' + actualParticipantCount);

                const participants = {
                    'names': response.name,
                    'actualParticipantCount': actualParticipantCount
                }
                callback(participants);
            });
        }

        enableAppSharing() {
            const requestEnableAppShare = {
                type: 'RequestEnableAppShare',
                isEnable: true
            };

            this.vidyoClient.sendRequest(requestEnableAppShare, function (response) { });
        }

        clientShareGet(callback) {
            const request = <any>{};

            request.type = 'RequestGetWindowShares';

            // public properties for created object,
            // initial values of which are potentially passed
            // into this function
            request.requestType = ''; // params && params.requestType || "";
            request.remoteAppUri = ['']; // params && params.remoteAppUri || [""];
            request.remoteAppName = ['']; // params && params.remoteAppName || [""];
            request.numApp = 0; // params && params.numApp || 0;
            request.currApp = 0; // params && params.currApp || 0;
            request.eventUri = ''; // params && params.eventUri || "";
            request.newApp = 0; // params && params.newApp || 0;

            this.vidyoClient.sendRequest(request, callback);
        }

        displayNewShare(request) {
            // if there's a new share, display it
            if (this.newShareUri) {
                for (let i = 0; i < request.numApp; i++) {
                    if (request.remoteAppUri[i] === this.newShareUri) {
                        request.newApp = i + 1;
                        break;
                    }
                }
                // otherwise (when shares are removed), display the last share added
            } else {
                request.newApp = request.numApp;
            }
            request.type = 'RequestSetWindowShares';
            request.requestType = 'ChangeSharingWindow';

            // FIX: add response callback to check for success
            this.vidyoClient.sendRequest(request, null);
            if (this.useWebRTC) {
                const numberOfShares = request.newApp;
                if (numberOfShares > 0) {
                    this.adjustWebRTCLayoutWithShare();
                } else {
                    this.adjustWebRTCLayout();
                }
            }
        }

        clientSharesSetCurrent() {
            this.clientShareGet(this.displayNewShare.bind(this));
        }

        onOutEvent(event) {
            let logMesg;
            let waitingForConnection: boolean;
            event = event || {};
            let msg = `Received out event with type of ${event.type}`;
            this.log(msg);
            msg = 'Event: ' + JSON.stringify(event);
            this.log(msg);
            switch (event.type) {
                case 'OutEventSignIn':
                    if (parseInt(event.activeEid, 10) === 0) {
                        const licenseEvent = {
                            'type': 'InEventLicense'
                        };
                        this.vidyoClient.sendEvent(licenseEvent, null);
                        this.log('Sent InEventLicense');
                    } else {
                        this.log('Received activeEid: ' + event.activeEid);
                        // When using NPAPI plugin, may encounter instances where send events were issued before
                        // plugin is started. In that case, we queue the events and then resend once
                        // plugin is started and we are at point the events are safe to send
                        if (this.inEventQueueForSend.length > 0) {
                            for (let index = 0; index < this.inEventQueueForSend.length; index++) {
                                const inEventToSend = this.inEventQueueForSend[index];
                                this.vidyoClient.sendEvent(inEventToSend);
                            }
                            this.inEventQueueForSend = [];
                        }
                    }
                    break;
                case 'OutEventPluginConnectionSuccess':
                    waitingForConnection = false;
                    if (this.isChrome && !this.useWebRTC) {
                        this.vidyoClient.sendRequest({
                            type: 'RequestSetDomain',
                            'domain': this.urlPortalAddress
                        }, function (response) {
                            if (response.result !== 'ok') {
                                this.log('RequestSetDomain: Error setting domain');
                            }
                        });
                    }
                    break;
                case 'OutEventPluginConnectionFailure':
                    waitingForConnection = false;
                    if (!this.isChrome || !this.isSmartVideoAgent) {
                        const stoppedPluginFailure = this.vidyoClient.stop();
                    }
                    break;
                case 'OutEventPluginError':
                    waitingForConnection = false;
                    if (!this.isChrome || !this.isSmartVideoAgent) {
                        const stoppedPluginError = this.vidyoClient.stop();
                    }
                    break;
                case 'OutEventReloaded':
                    waitingForConnection = false;
                    if (!this.isChrome || !this.isSmartVideoAgent) {
                        const stoppedReloaded = this.vidyoClient.stop();
                    }
                    break;
                case 'extensionMsgClientClosed':
                    if (event.message === 'Native host has exited.') {
                        this.log('VidyoClientForWeb stopped for some reason');
                    } else if (event.message === 'Specified native messaging host not found.') {
                        this.log('VidyoClientForWeb not Found or not installed');
                    }
                    break;
                // ---------typical OUT EVENTS-------------
                case 'OutEventCallState':
                    logMesg = `********* Call State Changed to: ${event.callState}**********`;
                    this.log(logMesg);
                    break;
                case 'OutEventLogicStarted':
                    if (this.willStartOnLoad) {
                        if (!this.useWebRTC) {
                            this.enableAppSharing();
                        }
                        this.sendGuestLogin();
                    }
                    break;
                case 'OutEventConferenceActive':
                    if (this.isChrome || this.useWebRTC || this.isSmartVideoAgent) {
                        this.log('Conference Active, useWebRTC is set to: ' + this.useWebRTC);
                        this.switchToConference();
                    }
                    if (this.useWebRTC) {
                        this.adjustWebRTCLayout();
                        break;
                    }
                    if (this.isChrome || this.isSmartVideoAgent) {
                        this.videoElem.style.width = '100%';
                        this.videoElem.style.height = '100%';
                        // Confirmed in conference, display video
                        this.videoElem.style.display = 'block';

                        document.getElementById('lbl_usermedia').style.display = 'none';
                    }

                    this.setPreferredMode(1);
                    this.setParticipantLimit(16);
                    this.sendAgentConnected();
                    break;
                case 'OutEventConferenceEnded':
                    this.conferenceModal.style.display = 'none';
                    this.setParticipantLimit(16);
                    // Assume want to stop the plugin after conference is over.
                    const stoppedConferenceEnded = this.vidyoClient.stop();
                    // Add an additional call to clean up/reset
                    this.vidyoEndedCallback();
                    break;
                case 'OutEventJoinProgress':
                    // WebRTC doesn't use OutEventJoinProgress, so only do progress for plugins
                    if (!this.useWebRTC) {
                        this.progressStep++;
                        logMesg = `Join Progress Step: ${this.progressStep}`;
                        this.log(logMesg);
                    }
                    break;
                case 'OutEventAddShare':
                    this.sharesInCall++;
                    logMesg = `Share added from URI: ${event.URI}`;
                    this.log(logMesg);
                    this.newShareUri = event.URI;
                    this.clientSharesSetCurrent();
                    this.setPreviewModeOnStatusChange();
                    break;
                case 'OutEventRemoveShare':
                    this.sharesInCall--;
                    if (this.sharesInCall === 0) {
                        this.fullScreenShare = false;
                        this.setParticipantLimit(16);
                    }
                    this.clientSharesSetCurrent();
                    this.setPreviewModeOnStatusChange();
                    break;
                case 'OutEventParticipantsChanged':
                    this.log('Participants changed, event: ' + JSON.stringify(event));
                    this.log('useWebRTC is set to ' + this.useWebRTC);
                    if (!this.useWebRTC) {
                        // this.participantsInCall = event.participantCount;
                        this.clientGetParticipants(participants => {
                            this.participantsInCall = participants.actualParticipantCount;
                        })
                        this.setPreviewModeOnStatusChange();
                    } else {
                        this.log('Adjusting WebRTC layout due to participant change');
                        this.adjustWebRTCLayout();
                    }
                    break;
                case 'OutEventVideoStreamsChanged':
                    this.log('Video Streams changed, event: ' + JSON.stringify(event));
                    // this.participantsInCall = event.streamCount + 1;
                    this.adjustWebRTCLayout();
                    break;
                case 'OutEventActiveTalkersChanged':
                    this.adjustWebRTCLayout();
                    break;
                case 'PrivateOutEventVcsoapGuestLink':
                    this.log(`****** GuestID = ${event.guestID}********`);
                    this.log(JSON.stringify(event));
                    break;
                case 'OutEventLicense':
                    if (event.OutOfLicenses === true) {
                        console.error('Received license error');
                        console.error(event);
                    }
                    break;
                case 'VidyoOutEventCancelShare':
                    const lbl_sharetoggle = <HTMLElement>document.getElementById('lbl_share_toggle');
                    const sharetoggle = <HTMLInputElement>document.getElementById('share_toggle');
                    lbl_sharetoggle.innerText = 'Click to Share';
                    sharetoggle.checked = false;
                    // this.toggleShareIcon();
                    break;
                // please add additional OutEvents as needed
                case 'OutEventRemoteSourceAdded':
                    const eventDisplayName = <string>event.displayName;
                    const eventDisplayNameArray = eventDisplayName.split('-');
                    const inEvent = {
                        'type': 'InEventSetDisplayName',
                        'uri': event.participantURI,
                        'displayName': ''
                    };
                    // Remove the -Customer suffix when displaying name
                    if (event.displayName.indexOf('-Customer') !== -1) {
                        const newDisplayName = eventDisplayName.substring(0, eventDisplayName.indexOf('-Customer'));
                        inEvent.displayName = newDisplayName;
                        this.vidyoClient.sendEvent(inEvent);
                        return;
                    }
                    // Remove the InteractionId when displaying name
                    eventDisplayNameArray.forEach(element => {
                        if (element.length === 10 && !isNaN(+element)) {
                            inEvent.displayName = eventDisplayNameArray[0];
                            this.vidyoClient.sendEvent(inEvent);
                            return;
                        }
                    });
                    break;
                default:
                    this.log('Did not have a matching case for OutEvent: ' + event.type);
                    break;
            }
        }

        onDoubleClickChrome() {
            if (this.sharesInCall > 0) {
                if (this.fullScreenShare) {
                    this.setParticipantLimit(16);
                    this.fullScreenShare = false;
                } else {
                    this.setParticipantLimit(0);
                    this.fullScreenShare = true;
                }
                this.setPreviewModeOnStatusChange();
            }
        }

        addPluginToDom() {

            // don't add if already added
            const pluginElement = document.getElementById('plugin');
            if (!pluginElement) {
                this.log('Attempting to add plugin to the DOM...');

                let pluginMimeType = 'application/x-vidyoweb-';
                pluginMimeType += this.pluginVersion;

                const pluginObject = <HTMLObjectElement>document.createElement('object');
                pluginObject.className = 'plugin';
                pluginObject.id = 'plugin';
                pluginObject.width = '100%';
                pluginObject.height = '100%';

                const holder = document.getElementById('pluginHolder');

                try {
                    pluginObject.type = pluginMimeType;
                    this.log('Plugin type successfully set to: ' + pluginMimeType);
                } catch (error) {
                    const mesg = `Unable to Instantiate VidyoWeb: ${pluginMimeType}; Error: ${error}`;
                    this.log(mesg);
                    return false;
                }

                if (!pluginObject.parentNode) {
                    holder.appendChild(pluginObject);
                    this.log('Plugin added to DOM');
                }

                if (!pluginObject['start']) {
                    /* Keep object in the DOM. Browsers commonly invoke "Click to Play" functionality
                    which causes pluginObject.start to fail, but plugin will be loaded when user clicks
                    to allow/trust */
                    this.log('Could not find start property on Plugin, probably due to plugin not having permissions to run yet');
                }
            }

            return true;
        }

        startPlugin() {
            this.log('startPlugin() called');
            this.switchToConference();
            if (this.addPluginToDom()) {
                const pluginHtml = document.getElementById('plugin');
                const config = {
                    plugin: pluginHtml,
                    outEventCallbackObject: this,
                    defaultOutEventCallbackMethod: 'onOutEvent',
                    useCallbackWithPlugin: this.useCallbackForOutEvents
                };
                if (pluginHtml['start']) {
                    clearTimeout(this.timer);
                    if (this.vidyoClient.start(config)) {
                        this.log('VidyoWeb started successfully');
                    } else {
                        this.log('VidyoWeb did not start successfully!');
                    }
                } else {
                    // Retry startPlugin, in case plugin needs to be activated before plugin.start will work
                    this.log('plugin.start not yet available in startPlugin(), retrying in 1 second');
                    this.timer = setTimeout(this.startPlugin.bind(this), 1000);
                }

            } else {
                this.log('addPluginToDom returned false');
                this.log('VidyoWeb did not start successfully!');
            }
        }

        startWebRTCClient() {
            this.vidyoClient.setDefaultOutEventCallbackMethod('onOutEvent');
            this.vidyoClient.setOutEventCallbackObject(this);
            this.vidyoClient.setLogCallback(this.log.bind(this));
            this.vidyoClient.setSessionManager(this.webRTCSessionManager);
            this.vidyoClient.start();
        }

        startChromeWrapper() {
            if (!this.chromeWrapper) {
                this.chromeWrapper = new ChromeWrapper(this.browser);
            }
            this.vidyoClient.setProxy(this.chromeWrapper.chromeproxy);
            this.chromeWrapper.chromeproxy.connect();
            this.chromeWrapper.chromeproxy.setOutEventDispatchMethod(this.onOutEvent.bind(this));

            this.chromeWrapper.chromeproxy.start(this.chromeNMSVersion, startResult => {
                const result = JSON.stringify(startResult.result);
                this.log(`ChromeProxy.start: ${result}`);
            }, null);

            if (!this.chromeWrapper.chromeproxy.started) {
                this.detectCameraStatusAndContinue();
            } else {
                this.startChromeVideo();
            }
        }

        startChromeVideo() {
            this.log('startChromeVideo called');
            let retval = false;
            const holder = document.getElementById(this.pluginHolder);
            holder.innerHTML = '<video class="video" autoplay></video>' +
                '<label id="lbl_usermedia" style="position: relative; color: #ddd;' +
                'top: -50%; margin-left: 20px; font-size: 20px; font-family: arial;">' +
                'Make sure to allow the browser to use your camera.</label>';

            const usermediaMessage = document.getElementById('lbl_usermedia');
            this.videoElem = <HTMLVideoElement>holder.childNodes[0];
            this.videoElem.addEventListener('dblclick', this.onDoubleClickChrome.bind(this), false);

            // Don't display the video element until we've confirmed we are in the conference
            this.videoElem.style.display = 'none';

            this.chromeWrapper.chromeproxy.useVideoElem(this.videoElem, (message, permissionDeniedError) => {
                usermediaMessage.innerHTML = message;
                if (!permissionDeniedError && !message) {
                    retval = true;
                } else if (permissionDeniedError) {
                    window.alert('VidyoWeb needs access to your camera in order to join a conference.' +
                        'If you change your mind, click the camera icon in your url bar and allow the browser to use your devices');
                    // Should not actually ever reach this part of code if they have a camera
                } else {
                    window.alert('VidyoWeb could not start your camera. Please check your camera is plugged in and then press OK.');
                    this.detectCameraStatusAndContinue();
                }
            });

            return retval;
        }

        detectCameraStatusAndContinue() {
            this.vidyoClient.sendRequest({ type: 'RequestGetCameraStatus' }, cameraReady => {
                /*Note: Need to check this code. It appears we get a status even if a camera isn't actually plugged in
                This is likely a bug in Vidyo's code, where it's saying/returning the *WebVirtualCamera* is ready, but what
                we want is the status of whether that virtual camera has successfully connected to a physical camera*/
                if (cameraReady.status) {
                    console.log('Camera status check returned: ' + cameraReady.status);
                    this.startChromeVideo();
                } else {
                    // loop until camera detected
                    console.log('Camera not detected, rechecking in two seconds...');
                    setTimeout(this.detectCameraStatusAndContinue.bind(this), 2000);
                }
            });
        }

        setParticipantLimit(count) {
            if (count > 8 || count < 0) {
                count = 8;
            }

            const inEvent = {
                'type': 'InEventParticipantsLimit',
                'maxNumParticipants': count
            };

            this.vidyoClient.sendEvent(inEvent, null);
        }

        setPreferredMode(preferred) {
            const inEvent = <any>{
                'type': 'InEventLayout'
            };

            inEvent.numPreferred = preferred;
            this.vidyoClient.sendEvent(inEvent, null);

        }

        setPreviewMode(previewMode) {
            const inEvent = <any>{
                'type': 'EventPreview'
            };
            this.log('Setting preview mode to: ' + previewMode);
            inEvent.previewMode = previewMode;
            this.vidyoClient.sendEvent(inEvent, null);

        }

        setPreviewModeOnStatusChange() {
            if (isNaN(this.participantsInCall)) {
                this.log('Participants in call isn\'t defined for some reason');
            }

            this.log('Participants in Call: ' + this.participantsInCall)
            if (this.fullScreenShare) {
                this.setPreviewMode('PIP');
            } else {
                if (this.participantsInCall <= 3) {
                    if (this.sharesInCall > 0) {
                        this.setPreviewMode('Dock');
                    } else {
                        this.setPreviewMode('PIP');
                    }

                } else {
                    this.setPreviewMode('Dock');
                }
            }
        }

        sendGuestLogin() {

            if (!this.urlPortalUri) {
                return 0;
            }

            if (!this.urlGuestName) {
                this.urlGuestName = 'Guest';
            }
            // replace ALL occurrences of "+" in Guest name (in case of multiple spaces
            this.urlGuestName = this.urlGuestName.replace(/\+/g, ' ');
            const requestId = Math.floor(Math.random() * 1000000);

            const inEvent = <any>{
                'type': 'PrivateInEventVcsoapGuestLink',
                'typeRequest': 'GuestLink',
                'requestId': requestId,
                'portalUri': this.urlPortalAddress,
                'roomKey': this.urlRoomKey,
                'guestName': this.urlGuestName,
                'loginType': 'guest'
            };

            if (this.urlRoomPin) {
                inEvent.pin = this.urlRoomPin;
                this.log('sendGuestLink passed : ' + this.urlRoomPin + ', ' + this.urlPortalAddress + ', ' +
                    this.urlRoomKey + ', ' + this.urlGuestName)
            }
            this.log('Sending guest login: ' + JSON.stringify(inEvent));
            this.vidyoClient.sendEvent(inEvent);
            return 0;
        }

        sendCustomerConnected() {
            const url = this.config.SVSIntegrationServiceUrl + '/ininvid/v1/customerconnected';
            const request = <any>{};
            request.RoomKey = this.urlRoomKey;

            if (!this.isJoinRequest) {
                const vidyoSessionData = JSON.parse(window.localStorage.getItem('VidyoSessionData'));
                request.InteractionId = vidyoSessionData.interactionId;
                this.log('Sending customer connected request: ' + JSON.stringify(request));
                Ajax.ajaxSendRequest(url, 'POST', request);
            }
        }

        sendAgentConnected() {
            const url = this.config.SVSIntegrationServiceUrl + '/ininvid/v1/agentconnected';
            const request = <any>{};
            request.RoomKey = this.urlRoomKey;

            if (this.isJoinRequest && this.urlInteractionId) {
                request.InteractionId = this.urlInteractionId;
                this.log('Sending agent connected request: ' + JSON.stringify(request));
                Ajax.ajaxSendRequest(url, 'POST', request);
            }
        }

        sendDisconnect() {
            const url = this.config.SVSIntegrationServiceUrl + '/ininvid/v1/disconnect';
            const request = <any>{};
            let isCustomer;
            if (this.isJoinRequest) {
                isCustomer = false;
            } else {
                isCustomer = true;
            }
            request.RoomKey = this.urlRoomKey;
            request.IsCustomer = isCustomer;

            if (isCustomer) {
                // Send the interactionId, in case the Ajax request gets routed to a server that
                // isn't currently aware of the interaction
                const vidyoSessionData = JSON.parse(window.localStorage.getItem('VidyoSessionData'));
                request.InteractionId = vidyoSessionData.interactionId;
            } else {
                // This user joined the video call
                // The Vidyo AddIn in IC client appends the IC user's interactionId to the URL that is popped
                request.InteractionId = this.urlInteractionId;
            }
            this.log('Sending disconnect request: ' + JSON.stringify(request));
            Ajax.ajaxSendRequest(url, 'POST', request);

        }

        sendLeaveEvent(splashElement: string, pluginHolderElement: string, vidyoAreaElement: string) {
            const inEvent = {
                'type': 'InEventLeave'
            };

            this.vidyoClient.sendEvent(inEvent, null);
            this.sendDisconnect();
            this.vidyoEndedCallback();
        }

        sendMuteCameraEvent(mute: boolean) {
            const inEvent = {
                'type': 'InEventMuteVideo',
                'willMute': true
            };
            inEvent.willMute = mute;
            if (this.vidyoClient.sendEvent(inEvent, null)) {
                this.log('Send Mute Camera sent successfully');
            } else {
                this.log('Send Mute Camera failed');
                this.log('Send Mute Camera failure may be due to plugin not being started yet, queueing event');
                this.inEventQueueForSend.push(inEvent);
            }
        }

        sendMuteMicEvent(mute: boolean) {
            const inEvent = {
                'type': 'InEventMuteAudioIn',
                'willMute': true
            };
            inEvent.willMute = mute;

            if (this.vidyoClient.sendEvent(inEvent, null)) {
                this.log('Send Mute Microphone sent successfully');
            } else {
                this.log('Send Mute Microphone failed');
                this.log('Send Mute Microphone failure may be due to plugin not being started yet, queueing event');
                this.inEventQueueForSend.push(inEvent);
            }
        }

        sendMuteSpeakerEvent(mute: boolean) {
            const inEvent = {
                'type': 'InEventMuteAudioOut',
                'willMute': true
            };
            inEvent.willMute = mute;

            if (this.vidyoClient.sendEvent(inEvent, null)) {
                this.log('Send Mute Speaker sent successfully');
            } else {
                this.log('Send Mute Speaker failed');
                this.log('Send Mute Speaker failure may be due to plugin not being started yet, queueing event');
                this.inEventQueueForSend.push(inEvent);
            }
        }

        sendShareEvent(shareId) {
            let shareType: string;
            let window = shareId;

            if (this.sysDesktopId.indexOf(shareId) !== -1) {
                shareType = 'DesktopWindow';
                if (this.isChrome || this.isSmartVideoAgent) {
                    window = this.sysDesktopId.indexOf(shareId);
                }
            } else {
                shareType = 'ApplicationWindow';
            }
            const inEvent = {
                'type': 'InEventShare',
                'window': window,
                'shareType': shareType
            };

            this.vidyoClient.sendEvent(inEvent, null);
        }

        sendUnshareEvent() {
            const inEvent = {
                'type': 'InEventUnshare'
            };

            this.vidyoClient.sendEvent(inEvent, null);
            this.updateShareList(null);
        }

        getUrlVars(url) {
            let match;
            // Regex to find the space substitutions
            const pl = /(\+|_)/g,
                search = /([^&=]+)=?([^&]*)/g,
                decode = function (s) {
                    // Return spaces to string, trim extraneous spaces
                    return decodeURIComponent(s.replace(pl, ' ').replace(/\s+/g, ' ').replace(/^\s+|\s+$/, ''));
                },
                query = url.substring(url.indexOf('?') + 1);

            const urlParams = {};
            while ((match = search.exec(query)) !== null) {
                urlParams[decode(match[1])] = decode(match[2]);
            }

            return urlParams;
        }

        parseUrlArgs(url) {
            this.log('Parsing arguments from URL: ' + url);
            const urlParams = this.getUrlVars(url);
            this.log('Arguments parsed: ' + JSON.stringify(urlParams));
            let portalRes;
            this.urlPortalUri = urlParams['portalUri'];
            this.urlRoomPin = urlParams['roomPin'];
            this.urlEncoded = urlParams['encoded'];
            this.urlRoomKey = urlParams['key'];
            this.urlInteractionId = urlParams['interactionId'];
            if (urlParams['isVideoMuted']) {
                this.urlMuteVideoOnJoin = (urlParams['isVideoMuted'].toLowerCase() === 'true');
            }
            if (urlParams['isAudioMuted']) {
                this.urlMuteAudioOnJoin = (urlParams['isAudioMuted'].toLowerCase() === 'true');
            }

            if (this.isJoinRequest && this.urlInteractionId) {
                // This is an IC agent, append the InteractionId to guest name so we can track on the SmartVideo server
                this.urlGuestName = urlParams['guestName'] + '-' + this.urlInteractionId;
            } else if (!this.isJoinRequest) {
                // This is the customer's request for a video call, append -Customer to guest name so we can track on the SmartVideo server
                this.urlGuestName = urlParams['guestName'] + '-Customer';
            } else {
                // This is someone joining who was invited to conference via GuestLink
                this.urlGuestName = urlParams['guestName'];
            }
            this.log(urlParams);
            this.log('parseUrlArgs set roomKey to: ' + this.urlRoomKey + ', roomPin to: ' +
                this.urlRoomPin + ', guestName to: ' + this.urlGuestName);
            const isMobileUrl = url.indexOf('mobile')
            if (isMobileUrl <= 0) {
                if (this.urlPortalUri.indexOf('flex') > -1) {
                    portalRes = this.urlPortalUri.split('\/flex.html\?roomdirect.html');
                    this.urlPortalAddress = portalRes[0];
                } else if (this.urlPortalUri.indexOf('join') > -1) {
                    portalRes = this.urlPortalUri.split('\/join');
                    this.urlPortalAddress = portalRes[0];
                }
            } else {
                portalRes = this.urlPortalUri.split('\/mobile.html');
                this.urlPortalAddress = portalRes[0];
            }
            this.log('urlPortalAddress set to: ' + this.urlPortalAddress);

        }

        timeStamp() {
            const now = new Date();
            const date = [now.getMonth() + 1, now.getDate(), now.getFullYear()];
            const time = [now.getHours(), now.getMinutes(), now.getSeconds()];
            const suffix = (time[0] < 12) ? 'AM' : 'PM';
            time[0] = (time[0] < 12) ? time[0] : time[0] - 12;
            time[0] = time[0] || 12;

            for (let i = 1; i < 3; i++) {
                if (time[i] < 10) {
                    time[i] = <any>(`0${time[i]}`);
                }
            }

            // Return the formatted string
            return date.join('/') + ' ' + time.join(':') + ' ' + suffix;
        }

        log(msg) {
            if (this.useConsoleForLogging) {
                console.log(this.timeStamp() + '  ' + msg);
            }
            if (this.useAlertsForLogging) {
                alert(msg);
            }
        }

        cancelContextMenu() {
            return false;
        }

        adjustWebRTCLayout() {
            let participantCount = 1;
            // The number of participants in the conference can be different from those that
            // are actually active in conference. For example, can have participants like Recorder, Supervisor,
            // etc., that are in conference and therefore count as participants, but don't have audio/video streams
            this.vidyoClient.sendRequest({ type: 'RequestActiveTalkers' }, function (request) {
                // When a conference starts, activeTalkers may not be populated yet, so make sure we at least return a value of 1.
                participantCount = request.numActiveTalkers;
                console.log('Active Talkers found: ' + participantCount);
                // Check for NaN or less than one
                if (participantCount !== participantCount || participantCount < 1) {
                    participantCount = 1;
                }
            });
            this.participantsInCall = participantCount;

            if (this.sharesInCall > 0) {
                this.adjustWebRTCLayoutWithShare();
            } else {
                const self = this;
                // Hide the share Div
                document.getElementById('shareVideoDiv').style.display = 'none';
                // Resize participants box to full
                document.getElementById('participants_box').className = 'participants-box';
                // We get participants directly, because sometimes we don't reliably get out events
                // For example, when joining conference we sometimes do not get OutEventVideoStreamsChanged, even though we should
                /* this.vidyoClient.sendRequest({ type: 'RequestGetNumParticipants' }, function (request) {
                    // Subtract one from count, since the local participant video is handled separately from remote participant streams
                    participantCount = request.numParticipants - 1;
                }); */

                this.log('RequestGetNumParticipants returned ' + participantCount + ' participants');
                this.log('adjustWebRTCLayout: Participants in call: ' + this.participantsInCall);
                const participantTest = false;

                //  For testing purposes only
                /*participantDivsCount = 8;
                participantTest = true;*/

                let DivHeight;
                let participantDivClassName;



                if (participantCount <= 1) {
                    participantDivClassName = 'participant-100-wrapper';
                    DivHeight = '100%';
                } else if (participantCount === 2 || participantCount === 4) {
                    participantDivClassName = 'participant-50-wrapper';
                    DivHeight = '50%';
                } else {
                    participantDivClassName = 'participant-33-wrapper';
                    DivHeight = '33%';
                }

                if (participantCount < 1) {
                    participantCount = 1;
                }

                // Hide all participants before applying changes
                this.log('Hiding all the participants');
                const participantsBox = document.getElementById('participants_box');
                const participantDivs = participantsBox.children;
                for (let index = 0; index < participantDivs.length; index++) {
                    const participantDiv = <HTMLDivElement>participantDivs[index];
                    participantDiv.className = 'hide';
                    // Setting className to hide should cause display to be set to 'none', however this doesn't always work
                    participantDiv.style.display = 'none';
                }

                this.log('Resizing participants');
                this.log('Participant DivHeight calculated at ' + DivHeight);
                for (let index = 0; index < participantCount; index++) {
                    const participantDiv = document.getElementById('participantDiv' + index.toString());
                    participantDiv.className = participantDivClassName;
                    const video = <HTMLVideoElement>document.getElementById('remoteVideo' + index.toString());
                    // When sharing under Firefox, we use inline-block, so switch back to flex after sharing done
                    if (!(participantDiv.style.display === 'flex')) {
                        participantDiv.style.display = 'flex';
                    }
                    video.style.display = 'none';
                    setTimeout(function () {
                        video.style.display = 'block';
                        // Firefox totally screws up sizing the participant Div, so we force a sane size based on the video width
                        if (self.isFireFox) {
                            const videowidthpx = (video.offsetWidth + 1).toString() + 'px';
                            // participantDiv.style.width = videowidthpx;
                            participantDiv.style.width = videowidthpx;
                            console.log('Participant Div' + index + ' resized to ' + participantDiv.style.width);
                        }
                    }, 200);

                    if (participantTest) {
                        const participantTitle = document.getElementById('participant' + index.toString());
                        // Real participants have their media streams assigned as srcObject on video player
                        // If srcObject doesn't exist, then simulate the participant
                        if (!video.srcObject) {
                            participantTitle.innerText = 'Simulated Participant ' + index.toString();
                            video.src = this.ImagePath + 'SampleVideo_720x480_10mb.mp4';
                        }
                    }
                }
            }
        }

        adjustWebRTCLayoutWithShare() {
            let participantCount = 1;
            // The number of participants in the conference can be different from those that
            // are actually active in conference. For example, can have participants like Recorder, Supervisor,
            // etc., that are in conference and therefore count as participants, but don't have audio/video streams
            this.vidyoClient.sendRequest({ type: 'RequestActiveTalkers' }, function (request) {
                // When a conference starts, activeTalkers may not be populated yet, so make sure we at least return a value of 1.
                participantCount = request.numActiveTalkers;
                console.log('Active Talkers found: ' + participantCount);
                // Check for NaN or less than one
                if (participantCount !== participantCount || participantCount < 1) {
                    participantCount = 1;
                }
            });
            this.participantsInCall = participantCount;
            // Show the share Div
            document.getElementById('shareVideoDiv').style.display = 'block';
            document.getElementById('participants_box').className = 'participants-box-share';
            /* const participantSelectors = 'div.participant-wrapper, div.participant-33-wrapper,' +
                ' div.participant-50-wrapper, div.participant-100-wrapper, div.participant-share-wrapper';
            const participantNodes = document.querySelectorAll(participantSelectors);
            const participantDivsCount = participantNodes.length; */

            for (let index = 0; index < participantCount; index++) {
                const participantDiv = document.getElementById('participantDiv' + index.toString());
                participantDiv.className = 'participant-share-wrapper';
                const video = <HTMLVideoElement>document.getElementById('remoteVideo' + index.toString());
                // Firefox totally screws up sizing the participant Div, so we force a sane size based on the video width
                if (this.isFireFox) {
                    participantDiv.style.display = 'inline-block';
                    const videowidthpx = (video.offsetWidth + 1).toString() + 'px';
                    participantDiv.style.width = videowidthpx;
                }
            }
        }

        switchToConference() {
            document.getElementById('videoStartMessage').style.display = 'none';
            let acdModal = null;
            let conferenceModal = null;
            const fullmodals = document.getElementsByTagName('adapt-fullmodal');
            for (let i = 0; i < fullmodals.length; i++) {
                if (fullmodals[i].firstElementChild.className === 'youtube-container') {
                    acdModal = fullmodals[i];
                }
                if (fullmodals[i].firstElementChild.id === 'webRTCConferenceDiv' ||
                    fullmodals[i].firstElementChild.id === 'vidyoConferenceDiv') {
                    this.log('Showing conference modal');
                    conferenceModal = <HTMLElement>fullmodals[i];
                }
            }
            if (acdModal !== null) {
                document.body.removeChild(acdModal);
            }
            if (conferenceModal !== null) {
                conferenceModal.style.display = 'block';
                // Toggle camera and mic if mute options passed in
                const cameraButton = document.getElementById('camera_toggle');
                const micButton = document.getElementById('microphone_toggle');
                if (this.urlMuteVideoOnJoin) {
                    cameraButton.click();
                }
                if (this.urlMuteAudioOnJoin) {
                    micButton.click();
                }
                this.conferenceModal = conferenceModal;
            }
        }
    }
}
